import React from 'react';
import { Row, Col, Overlay, Popover } from 'react-bootstrap';
import moment from 'moment-timezone';

import generateId from 'helpers/id';

import IntlDateTime from 'components/layout/IntlDateTime';
import TimePicker from 'components/forms/TimePicker';

import ReactDatePicker from 'react-datepicker'
import { registerLocale, setDefaultLocale } from  "react-datepicker";
import { de } from 'date-fns/locale/de';
import { enUS } from 'date-fns/locale/en-US';
import { es } from 'date-fns/locale/es';
import { fr } from 'date-fns/locale/fr';
import { it } from 'date-fns/locale/it';
import { nl } from 'date-fns/locale/nl';
import { pl } from 'date-fns/locale/pl';
import { tr } from 'date-fns/locale/tr';

const availableLocales = {
  de: de,
  en: enUS,
  es: es,
  fr: fr,
  it: it,
  nl: nl,
  pl: pl,
  tr: tr,
};


export default class DateTimePicker extends React.Component {
  constructor(props) {
    super(props);

    moment.locale(globalLocale);

    this.state = {
      //value: moment.tz(props.value, props.returnTz).toISOString(),
      selectedDate: props.value ? moment.tz(props.value, props.returnTz).toDate() : null,

      focused: false,
      pickerId: generateId('date-picker-'),
      mouseOver: false,
    };

    this.inputRef = React.createRef();
  }

  componentDidMount() {
    this.registerDatePickerLocale(this.props.locale);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.locale !== this.props.locale) {
      this.registerDatePickerLocale(this.props.locale);
    }
  }

  registerDatePickerLocale = (localeCode) => {
    if (availableLocales[localeCode]) {
      registerLocale(localeCode, availableLocales[localeCode]);
    } else {
      console.warn(`Locale "${localeCode}" not supported. Falling back to default.`);
      if (!availableLocales['de']) {
        registerLocale('de', de);
      }
      registerLocale(localeCode, availableLocales['de']);
    }
  };

  checkFocused = () => {
    return this.state.focused ? 'focused ' : '';
  };

  checkRequired = () => {
    return this.props.meta.validate.rules.indexOf('required') !== -1 ? 'required ' : '';
  };

  checkError = () => {
    return this.props.validation.valid ? '' : 'has-error ';
  };

  // check if user may read this field
  checkAccess = () => {
    return this.props.meta.acl.read;
  };

  // check if user may write to this field
  checkReadOnly = () => {
    return !this.props.meta.acl.write;
  };

  resetDate = e => {
    e.preventDefault();
    e.stopPropagation();

    const { name, onChange } = this.props;
    onChange(name, null);

    this.setState({
      selectedDate: null,
    });
  };

  handleDateChange = date => {
    this.setState({ selectedDate: date }, () => {
      if (date) {
        const momentObj = moment.tz(date, this.props.displayTz).tz(this.props.returnTz);
        this.props.onChange(this.props.name, momentObj);
      } else if (this.props.showClearDate) {
        this.props.onChange(this.props.name, null);
      }
    });
  };

  handleFocusChange = (focused) => {
    this.setState({ focused });
  };

  checkDisabled = () => {
    const { disabled, meta } = this.props;
    if (disabled) {
      return true;
    }
    if (!meta.acl.write) {
      return true;
    }
    return false;
  };


  isOutsideRange = date => {
    if (this.props.minDate) {
      const minDate = moment.tz(this.props.minDate, 'UTC');
      if (date.isBefore(minDate)) {
        return true;
      }
    }
    if (this.props.maxDate) {
      const maxDate = moment.tz(this.props.maxDate, 'UTC');
      if (date.isAfter(maxDate)) {
        return true;
      }
    }

    return false;
  };


  onFocus = () => {
    this.setState({ focused: true });
  };

  onBlur = () => {
    this.setState({ focused: false });
  };

  onMouseEnter = () => {
    this.setState({ mouseOver: true });
  };

  onMouseLeave = () => {
    this.setState({ mouseOver: false });
  };


  render() {
    const {
      label,
      className,
      style,
      isDayBlocked,
      appendToBody,
      value,
      showClearDate,
      placeholder,
      locale,
      returnTz,
      displayTz,
      minDate,
      maxDate,
      timeInputLabel
    } = this.props;
    const { pickerId, focused, mouseOver, selectedDate } = this.state;

    const displayTzValue = moment.tz(value, returnTz).tz(displayTz);

    return (
      <div
        className={
          'cursor form-group form-group-default ' +
          this.checkFocused() +
          this.checkRequired() +
          this.checkError() +
          (this.checkDisabled() ? 'disabled ' : '') +
          ' ' +
          className
        }
        style={{ overflow: 'visible', paddingBottom: appendToBody ? '4px' : '3px', ...style }}
        onClick={this.onFocus}
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
      >
        <label>{label}</label>
        <div className="lh-25">
          <div className="clearfix">
            <span ref={this.inputRef}>{selectedDate ? <IntlDateTime date={moment(selectedDate).toISOString()} /> : placeholder || '-'}</span>
            {showClearDate && (
              <span className="pull-right">
                <a
                  href="#resetDate"
                  className={`text-danger p-l-5 p-r-5 ${mouseOver ? 'opacity-show ' : 'opacity-hide '}`}
                  onClick={this.resetDate}
                >
                  ×
                </a>
              </span>
            )}
          </div>

          <Overlay show={focused} target={this.inputRef.current} placement="bottom" rootClose onHide={this.onBlur}>
            <Popover id={pickerId} style={{ maxWidth: 'none', zIndex: 1200 }}>
              <Row>
                <Col xs={9} className="b-r b-grey">
                  <ReactDatePicker
                    selected={selectedDate}
                    onChange={this.handleDateChange}
                    //onFocus={() => this.handleFocusChange(true)}
                    //onBlur={() => this.handleFocusChange(false)}
                    //open={focused}
                    placeholderText={placeholder}
                    inline
                    dateFormat="dd.MM.yyyy HH:mm"
                    showTimeInput
                    timeInputLabel={timeInputLabel ?? "Time"}
                    locale={locale}
                    isClearable={showClearDate}
                    minDate={minDate ? moment.tz(minDate, 'UTC').toDate() : undefined}
                    maxDate={maxDate ? moment.tz(maxDate, 'UTC').toDate() : undefined}
                    disabled={this.checkDisabled()}
                  />
                </Col>
              </Row>
            </Popover>
          </Overlay>
        </div>
      </div>
    );
  }
}
DateTimePicker.defaultProps = {
  label: '',
  name: '',
  meta: { validate: { rules: '' }, acl: { read: true, write: true } },
  validation: { valid: true },
  value: null,
  onChange: () => {},
  disabled: false,
  displayTz: 'CET',
  returnTz: 'CET',
  className: '',
  style: {},
  locale: 'en',
  showClearDate: false,
};
